import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useEffect, useState } from "react";

import { AgentSection } from "@/components/dashboard/AgentSection";
import { CreditsSection } from "@/components/dashboard/CreditsSection";
import { SettingsContent } from "@/components/dashboard/SettingsContent";
import { SubscriptionSection } from "@/components/dashboard/SubscriptionSection";
import { Toast } from "@/components/ui/Toast";
import { api } from "@convex/_generated/api";
import { useBilling } from "@/hooks/useBilling";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { useDeleteAccount } from "@/hooks/useDeleteAccount";
import { useUser } from "@/hooks/useUser";

const TOAST_COPY = {
  success: "Payment received — your credits will appear here shortly.",
  canceled: "Checkout canceled — you haven't been charged.",
} as const;

export const Route = createFileRoute("/_authenticated/dashboard")({
  validateSearch: (search: Record<string, unknown>): { checkout?: "success" | "canceled" } =>
    search.checkout === "success" || search.checkout === "canceled"
      ? { checkout: search.checkout }
      : {},
  component: DashboardRoute,
});

function DashboardRoute() {
  const { user } = useUser();
  const { deleteAccount } = useDeleteAccount();
  const { balance, status: balanceStatus } = useCreditBalance();
  const billingInfo = useQuery(api.billing.getBillingInfo);
  const { startCheckout, openPortal, pendingAction, billingError } = useBilling();

  const plan = billingInfo?.plan ?? user?.plan ?? "trial";

  const { checkout } = Route.useSearch();
  const navigate = useNavigate();
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!checkout) return;
    setToast(TOAST_COPY[checkout]);
    // Strip the param so a refresh doesn't re-toast.
    void navigate({ to: "/dashboard", search: {}, replace: true });
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [checkout, navigate]);

  return (
    <div className="mx-auto max-w-7xl p-8">
      <h1 className="mb-2 font-heading text-4xl text-black dark:text-white">Settings</h1>
      <p className="mb-8 font-medium text-black/60 dark:text-white/60">Billing, subscription, and account</p>
      <SettingsContent
        agentSlot={
          <AgentSection
            plan={plan}
            onManagePlan={() =>
              document
                .getElementById("subscription-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          />
        }
        creditsSlot={
          <CreditsSection
            balance={balance}
            balanceStatus={balanceStatus}
            hasStripeCustomer={billingInfo?.hasStripeCustomer ?? false}
            pendingAction={pendingAction}
            billingError={billingError}
            onBuyPack={(kind) => void startCheckout(kind)}
            onOpenPortal={() => void openPortal()}
          />
        }
        subscriptionSlot={
          <SubscriptionSection
            plan={plan}
            planStatus={billingInfo?.planStatus ?? null}
            pendingAction={pendingAction}
            billingError={billingError}
            onSubscribe={() => void startCheckout("subscription")}
          />
        }
        onDeleteAccount={deleteAccount}
      />
      <Toast message={toast ?? ""} isVisible={toast !== null} />
    </div>
  );
}
